/**
 * This function overrides the core jQuery .html() function so that we can perform some DOM cleanup prior to calling the orignal .html() function.
 * This is being done to impprove overall app performance by attempting to keep the DOM node count, particularly for Kendo components, as low as possible.
 * Due to the nature of our Single-page Application (SPA), we have historically not been cleaning up DOM nodes and event listeners as we should, and we
 * have a pretty extensive DOM node leak as a result.
 */
var isAgModule = false;
(function ($) {
    let _oldHtml = $.fn.html;

    $.fn.html = function (htmlStr) {
        if ((htmlStr === '' && this.selector && this.selector !== '') || htmlStr === 'ag') {
            isAgModule = htmlStr === 'ag';
            let container = document.querySelector(this.selector);
            if (container && container.tagName === 'DIV' && !container.classList.contains('ah-dropcheck') && (window.localStorage.getItem('isExecuteDestroyDom') === null || window.localStorage.getItem('isExecuteDestroyDom') === "true")) {
                DevUtils.logDebug('destroying Kendos whenever $().html("") is called for a DIV, container [' + this.selector + ']', 'DomUtils $.fn.html()', { htmlStr: htmlStr }, 'orangered');
                // first do targeted destruction of specific Kendo components:
                DomUtils.destroyDomNodes(this.selector);
                // now do a generic destroy of Kendo components not found above (this will only destroy the widget, not the associated HTML code)
                DomUtils.destroyDomNodes(this.selector, true);
            }
        }

        return _oldHtml.apply(this, arguments);     // NOSONAR (use rest syntax to declare function args)
    };
})(jQuery);

/**+
 * Library of utility functions used to manage the DOM (specifically, to prevent DOM node leakage)
 */
const DomUtils = (function () {
    const _LOG_COLOR = 'orangered';
    const _DOM_DEV_MODE = window.localStorage.getItem('domDevMode') === 'true';

    return {
        destroyDomNodes: _destroyDomNodes,
        isDomDevMode: _isDomDevMode,
        logDomData: _logDomData
    };

    /**
     * Function to search and destroy various types of unwanted DOM components. This function should be called when certain types of navigation or
     * actions occur in the UI (e.g. navigate to a different page, close a dialog, etc.) so that the DOM nodes can be properly disposed of in
     * order to prevent DOM/memory leaks. Kendo controls in particular need to be explicitly destroyed when we are finished with them (they
     * only get destroyed automatically when URL navigation occurs, and since GC is a single page app of sorts, the URL never changes and hence
     * the Kendo components do not clean up after themselves). This function should be extended as needed to destroy other types of components.
     *
     * If you want to explicitly prevent a Kendo component from getting destroyed here, add CSS class "dom-no-destroy" to the Kendo component
     *
     * @param {string} containerSelector - CSS selector representing the container to which the search and destroy should be limited to
     *                                     e.g. pass in '.ah-modal' to search within a modal dialog (default: #MvcMainBody)
     * @param {boolean} nukeKendo - optional flag indicating you want to search and destroy all Kendo components within the specified container;
     *                              if true, containerSelector is also required as a safety precaution (default: false)
     */
    function _destroyDomNodes(containerSelector, nukeKendo, forceRefresh) {
        if (nukeKendo && containerSelector) {
            _nukeKendo(containerSelector);
        } else if (nukeKendo) {
            DevUtils.logWarn('nukeKendo was set to true but no containerSelector was passed in. No Kendo widgets will be destroyed.', 'DomUtils.destroyDomNodes()');
        } else {
            // only destroy dropdowns within a specific container, or within the main body if no container passed
            containerSelector = containerSelector ? containerSelector : '#MvcMainBody';

            _destroyKendoAutoCompletes(containerSelector);
            _destroyKendoCharts(containerSelector);
            _destroyKendoDatePickers(containerSelector);
            _destroyKendoDiagrams(containerSelector);
            _destroyKendoDropDownLists(containerSelector);
            _destroyKendoEditors(containerSelector);
            _destroyKendoGrids(containerSelector);
            _destroyKendoMultiSelects(containerSelector);
            _destroyKendoScheduler(containerSelector);
            _destroyKendoUploads(containerSelector);
            _destroyKendoToolTip(containerSelector);
        }

        if (forceRefresh) {
            window.location.reload();
        }
    }

    /**
     * Returns true if domDevMode is set to true in browser local storage. Used to dynamically display experimental test buttons
     * in the navigation page header for testing Destroy and Nuke functionality
     */
    function _isDomDevMode() {
        return _DOM_DEV_MODE;
    }

    ///////////////////////
    // private functions //
    ///////////////////////
    /**
     * This function destroys ALL kendo widgets that are children within the specified container
     *
     * NOTE: this will NOT remove the HTML elements from the DOM... it will only call destroy() on the Kendo widgets. We may
     * never want to use this because it blindly destroys ALL kendo components it finds within the container, and perhaps
     * more importantly it doesn't remove the extra bits of HTML elements from DOM.
     */
    function _nukeKendo(containerSelector) {
        _logDebug('Nuking all Kendo components within container [' + containerSelector + ']');
        kendo.destroy($(containerSelector)[0]);
    }

    function _destroyKendoCharts(containerSelector) {
        _logDebug('_destroyKendoEditors(): searching for kendoChart widgets to destroy within container [' + containerSelector + ']');
        let kendoWidgets = $(containerSelector + ' .k-chart[data-role="chart"]').not('.dom-no-destroy');
        _destroyKendoWidgets(containerSelector, 'kendoChart', kendoWidgets);
    }

    /**
     * This function will search for and destroy both KendoDatePicker and KendoDateTimePicker components
     */
    function _destroyKendoDatePickers(containerSelector) {
        _logDebug('_destroyKendoDatePickers(): searching for kendoDatePicker and kendoDateTimePicker widgets to destroy within container [' + containerSelector + ']');

        let kendoPickers = $(containerSelector + ' .k-datepicker [data-role="datepicker"], ' + containerSelector + ' .k-datetimepicker [data-role="datetimepicker"]').not('.dom-no-destroy');

        if (kendoPickers && kendoPickers.length > 0) {
            // loop through each kendo datepicker and destroy them
            kendoPickers.each(function (index, picker) {
                let kendoId = $(this).attr('id');
                let kendoType = $(this).closest(".k-datepicker").find("input[data-role='datepicker']").length > 0 ? 'kendoDatePicker' : 'kendoDateTimePicker';


                if (kendoId) {
                    let kendoPicker = $(this).data(kendoType);

                    if (kendoPicker !== undefined) {
                        _logDebug('_destroyKendoDatePickers(): destroying ' + kendoType + ' [' + kendoId + ']', picker);

                        kendoPicker.destroy();

                        // destroy the calendar bits attached to document.body
                        $('#' + kendoId + '_dateview').closest('.k-animation-container').remove();
                        $('#' + kendoId + '_dateview').closest('.k-calendar-container').remove();
                        $('#' + kendoId + '_timeview').closest('.k-animation-container').remove();
                        $('#' + kendoId + '_timeview').closest('.k-list-container').remove();
                        // destroy the calendar
                        picker.remove();
                    }
                }
            });
        } else {
            _logDebug('_destroyKendoDatePickers(): no kendoDatePicker or kendoDateTimePicker widgets found to destroy');
        }
    }

    /**
     * This function will search for and destroy kendoDiagram components
     */
    function _destroyKendoDiagrams(containerSelector) {
        _logDebug('_destroyKendoDiagrams(): searching for kendoDiagram widgets to destroy within container [' + containerSelector + ']');
        let kendoWidgets = $(containerSelector + ' .k-widget.k-diagram[data-role="diagram"]').not('.dom-no-destroy');
        _destroyKendoWidgets(containerSelector, 'kendoDiagram', kendoWidgets);
    }

    /**
     * This function will search, destroy and remove the leftover HTML elements of all Kendo DropDownList elements within the specified container
     */
    function _destroyKendoDropDownLists(containerSelector) {
        _logDebug('_destroyKendoDropDownLists(): searching for kendoDropDownList widgets to destroy within container [' + containerSelector + ']');

        // find all the kendoDropDownList components within the specified container
        let kendoDdls = $(containerSelector + ' .k-widget.k-dropdown').find('select[data-role="dropdownlist"], input[data-role="dropdownlist"] ,div[data-role="dropdownlist"]').not('.dom-no-destroy');

        if (kendoDdls && kendoDdls.length > 0) {
            _logDebug('_destroyKendoDropDownLists(): found ' + kendoDdls.length + ' potential kendoDropDownList widgets to destroy', kendoDdls);

            // loop thru each Kendo dropdown found within the container, and destroy them
            kendoDdls.each(function (index, ddl) {
                let kendoDdlId = $(this).attr('id');

                // the Kendo grids use a dropdown for pagination, and they don't have IDs... for now exclude these from destruction because the grid destruction code should handle it
                if (kendoDdlId) {
                    let kendoDdl = $(this).data("kendoDropDownList");

                    // if for some reason the Kendo has already been destroyed, it will be 'undefined' here
                    if (kendoDdl !== undefined) {
                        _logDebug('_destroyKendoDropDownLists(): destroying kendoDropDownList [' + kendoDdlId + ']', ddl);

                        // destroy the Kendo components that are appended to the document body that cannot clearly be associated to the widget (i.e. the expanded dropdown items)
                        kendoDdl.destroy();

                        // destroy the HTML
                        //kendoDdl.wrapper.remove();

                        // remove the dropdown HTML bits attached to the document.body (these are not attached to the dropdown and won't get deleted above)
                        if (!isAgModule) {
                            $('#' + kendoDdlId + '-list').closest('.k-animation-container').remove();
                            $('#' + kendoDdlId + '-list').closest('.k-list-container').remove();
                        }

                    }
                }
            });
        } else {
            _logDebug('_destroyKendoDropDownLists(): no kendoDropDownList widgets found to destroy');
        }
    }

    /**
     * This function will search, destroy and remove the leftover HTML elements of all Kendo Upload elements within the specified container.
     *
     * Example use case: Know menu > Health Article > Add > Description editor
     */
    function _destroyKendoEditors(containerSelector) {
        _logDebug('_destroyKendoEditors(): searching for kendoEditor widgets to destroy within container [' + containerSelector + ']');
        let kendoWidgets = $(containerSelector + ' .k-widget.k-editor').find('[data-role="editor"]').not('.dom-no-destroy');
        _destroyKendoWidgets(containerSelector, 'kendoEditor', kendoWidgets);
    }

    /**
     * This function will search, destroy and remove the leftover HTML elements of all Kendo Grid elements within the specified container
     */
    function _destroyKendoGrids(containerSelector) {
        _logDebug('_destroyKendoGrids(): searching for kendoGrid widgets to destroy within container [' + containerSelector + ']');

        // find all the Kendo Grid widgets within the specified container
        let kendoGrids = $(containerSelector + ' .k-widget.k-grid').not('.dom-no-destroy');

        if (kendoGrids && kendoGrids.length > 0) {
            _logDebug('_destroyKendoGrids(): found ' + kendoGrids.length + ' potential kendoGrid widgets to destroy', kendoGrids);

            // loop thru each Kendo dropdown found within the container, and destroy them
            kendoGrids.each(function (index, grid) {
                let gridId = $(this).attr('id');

                if (gridId) {
                    let kendoGrid = $(this).data('kendoGrid');

                    if (kendoGrid !== undefined) {
                        _logDebug('_destroyKendoGrids(): destroying kendoGrid [' + gridId + ']', grid);

                        // destroy the grid widget
                        kendoGrid.destroy();

                        // remove the pagination HTML bits attached to the document.body (these are not attached to the grid and won't get deleted down below)
                        let listBoxId = $('#' + gridId).find('.k-widget.k-dropdown[role="listbox"]').attr('aria-owns');
                        $('#' + listBoxId).closest('.k-animation-container').remove();
                        $('#' + listBoxId).closest('.k-list-container').remove();

                        // remove the grid HTML
                        $('#' + gridId).remove();
                    }
                }
            });
        } else {
            _logDebug('_destroyKendoGrids(): no kendoGrid widgets found to destroy');
        }
    }

    /**
     * This function will search, destroy and remove the leftover HTML elements of all Kendo MultiSelect elements within the specified container.
     *
     * Example use case: Admin menu > Manage Quality Measure > Add > Tax ID, NPI ID
     */
    function _destroyKendoMultiSelects(containerSelector) {
        _logDebug('_destroyKendoMultiSelects(): searching for kendoMultiSelect widgets to destroy within container [' + containerSelector + ']');
        let kendoWidgets = $(containerSelector + ' .k-widget.k-multiselect').find('select[data-role="multiselect"], input[data-role="multiselect"]').not('.dom-no-destroy');
        _destroyKendoWidgets(containerSelector, 'kendoMultiSelect', kendoWidgets, function (kendoId) {
            // remove the dropdown HTML bits attached to the document.body (these are not attached to the dropdown and won't get deleted above)
            $('.k-animation-container #' + kendoId + '-list'.wrapper).remove();
        });
    }

    function _destroyKendoScheduler(containerSelector) {
        _logDebug('_destroyKendoScheduler(): searching for kendoScheduler widgets to destroy within container [' + containerSelector + ']');
        let kendoWidgets = $(containerSelector + ' .k-widget.k-scheduler').not('.dom-no-destroy');
        _destroyKendoWidgets(containerSelector, 'kendoScheduler', kendoWidgets);
    }

    /**
     * This function will search, destroy and remove the leftover HTML elements of all Kendo Upload elements within the specified container.
     *
     * Example use case: Member > Activity Records > Consent Forms > Add > radio button Upload Form > choose Consent Form > choose Consent Was Provided > Select Files button
     */
    function _destroyKendoUploads(containerSelector) {
        _logDebug('_destroyKendoUploads(): searching for kendoUpload widgets to destroy within container [' + containerSelector + ']');
        let kendoWidgets = $(containerSelector + ' .k-widget.k-upload').find('input[data-role="upload"]').not('.dom-no-destroy');
        _destroyKendoWidgets(containerSelector, 'kendoUpload', kendoWidgets);
    }

    /**
    * This function will search, destroy and remove the leftover HTML elements of all Kendo AutoComplete elements within the specified container
    */
    function _destroyKendoAutoCompletes(containerSelector) {
        _logDebug('_destroyKendoAutoCompletes(): searching for kendoAutoComplete widgets to destroy within container [' + containerSelector + ']');

        // find all the KendoAutoComplete components within the specified container
        let kendoAutoComps = $(containerSelector + ' .k-widget.k-autocomplete').find('input[data-role=autocomplete]').not('.dom-no-destroy');

        if (kendoAutoComps && kendoAutoComps.length > 0) {
            _logDebug('_destroyKendoAutoCompletes(): found ' + kendoAutoComps.length + ' potential kendoAutoComplete widgets to destroy', kendoAutoComps);

            // loop through each Kendo AutoComplete found within the container, and destroy them
            kendoAutoComps.each(function (index, kac) {
                let kendoAutoCompId = $(this).attr('id');

                // the Kendo AutoComplete use a AutoComplete for pagination, and they don't have IDs... for now exclude these from destruction because the grid destruction code should handle it
                if (kendoAutoCompId) {
                    let kendoAutoComp = $(this).data("kendoAutoComplete");

                    // if for some reason the Kendo has already been destroyed, it will be 'undefined' here
                    if (kendoAutoComp !== undefined) {
                        _logDebug('_destroyKendoAutoCompletes(): destroying kendoAutoComplete [' + kendoAutoCompId + ']', kac);

                        // destroy the Kendo components that are appended to the document body that cannot clearly be associated to the widget (i.e. the expanded dropdown items)
                        kendoAutoComp.destroy();
                        // destroy the dropdown HTML
                        //kendoAutoComp.wrapper.remove();
                        $('#' + kendoAutoCompId + '-list').closest('.k-animation-container').remove();
                        $('#' + kendoAutoCompId + '-list').closest('.k-list-container').remove();
                    }
                }
            });
        } else {
            _logDebug('_destroyKendoAutoComplete(): no kendoAutoComplete widgets found to destroy');
        }
    }

    function _destroyKendoToolTip(containerSelector) {
        _logDebug('_destroyKendoToolTip(): searching for kendoTooltip widgets to destroy within container [' + containerSelector + ']');
        let kendoWidgets = $(containerSelector + ' .k-widget.k-tooltip').not('.dom-no-destroy');
        _destroyKendoWidgets(containerSelector, 'kendoTooltip', kendoWidgets);
    }
    function _destroyKendoWidgets(containerSelector, kendoWidgetName, kendoWidgets, postDestroyCallback) {
        if (kendoWidgets && kendoWidgets.length > 0) {
            _logDebug('_destroyKendoWidgets(): found ' + kendoWidgets.length + ' potential ' + kendoWidgetName + ' widgets to destroy', kendoWidgets);

            kendoWidgets.each(function (index, widget) {
                let kendoId = $(this).attr('id');
                let kendoWidget = $(this).data(kendoWidgetName);

                if (kendoWidget !== undefined) {
                    _logDebug('_destroyKendoWidgets(): destroying ' + kendoWidgetName + ' [' + kendoId + ']', widget);

                    kendoWidget.destroy();

                    // destroy the HTML
                    kendoWidget.wrapper.remove();

                    if (postDestroyCallback && typeof postDestroyCallback === 'function') {
                        postDestroyCallback(kendoId);
                    }
                }
            });
        } else {
            _logDebug('_destroyKendoWidgets(): no ' + kendoWidgetName + ' widgets found to destroy');
        }
    }

    function _logDebug(message, object) {
        DevUtils.logDebug(message, 'DomUtils', object, _LOG_COLOR);
    }

    function _logDomData(nav) {
        let domData = [];
        let input =
        {
            "navigation": nav,
            "domNode": document.querySelectorAll('*').length,
            "dateTime": new Date(),
            "patientId": ($("#selectPatientAccessed").val() === "" || $("#selectPatientAccessed").val() === null) ? VarPatientIdGlobal : $("#selectPatientAccessed").val()
        };
        let constData = {
            "Browser": browser,
            "Version": browserVersion,
            "MemberId": memberidval,
            "Details": []
        };
        let storageData = JSON.parse(window.sessionStorage.getItem('domData'));
        if (storageData !== null) {
            storageData[0].Details.push(input);
            window.sessionStorage.setItem('domData', JSON.stringify(storageData));
        }
        else {
            constData.Details.push(input);
            domData.push(constData);
            window.sessionStorage.setItem('domData', JSON.stringify(domData));
        }
    }
})();
